import React, { useEffect, useMemo, useRef, useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../../services/firebaseService/connection";
import { getWebsiteConfig } from "../../../services/firebaseService/endPoints/admin/website";
import { fbGetPartners } from "../../../services/firebaseService/endPoints/admin/partners";
import {
  postUpdateCDNUtil,
  invalidateStadiumCache,
} from "../../../helperFunctions/util";
import GroupQR from "../../../CommonComponents/GroupQR/GroupQR";
import {
  GUIDE_GROW,
  TOPIC_SETUP_CHANT_STADIUM,
} from "../../../constants/gettingStarted";
import ScreenHeader from "../../../CommonComponents/ScreenHeader";
import ChantButton from "../../../CommonComponents/ChantButton/ChantButton";
import ChantCheckbox from "../../../CommonComponents/ChantCheckbox/ChantCheckbox";
import ChantInput from "../../../CommonComponents/ChantInput/ChantInput";
import ChantSelect from "../../../CommonComponents/ChantSelect/ChantSelect";
import DescriptionText from "../../../CommonComponents/DescriptionText";
import SkeletonForm from "../../../CommonComponents/SkeletonLoaders/SkeletonForm/SkeletonForm";
import "./Stadium.css";
import useAdminDataStore, { selectCurrentGroupData } from "../../../store/adminData/adminData";

function StadiumSection({ id, title, children }) {
  return (
    <section id={id} className="stadium-setup-section">
      <h2 className="stadium-setup-section__title">{title}</h2>
      <div className="stadium-setup-section__body">{children}</div>
    </section>
  );
}

const StadiumSetup = () => {
  const groupData = useAdminDataStore(selectCurrentGroupData);

  const [publishChants, setPublishChants] = useState(true);
  const [isChantsPublished, setIsChantsPublished] = useState(false);
  const [publishPOM, setPublishPOM] = useState(false);
  const [publishPredictions, setPublishPredictions] = useState(false);
  const [publishDonations, setPublishDonations] = useState(false);
  const [joinUrl, setJoinUrl] = useState("");
  const [joinBenefit, setJoinBenefit] = useState("");
  const [donationPartner, setDonationPartner] = useState(null);
  const [donationBenefit, setDonationBenefit] = useState("");
  const [allPartners, setAllPartners] = useState([]);
  const [acceptContentResponsibility, setAcceptContentResponsibility] =
    useState(false);
  const [acceptRightReservations, setAcceptRightReservations] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const stadiumQrRef = useRef(null);
  const chantsQrRef = useRef(null);

  const groupName = groupData?.groupName ?? "";
  const groupKey = groupData?.groupKey ?? "";

  const stadiumPublicUrl = useMemo(() => {
    if (!groupKey) return "";
    return `https://stadium.chant.fan/${groupKey}`;
  }, [groupKey]);

  const chantsPublicUrl = useMemo(() => {
    if (!groupKey) return "";
    return `https://stadium.chant.fan/${groupKey}/chants`;
  }, [groupKey]);

  const partnerSelectOptions = useMemo(
    () =>
      allPartners.map((p) => ({
        label: p.name,
        value: String(p.id),
      })),
    [allPartners],
  );

  const fetchData = (groupId) => {
    if (!groupId) return;

    setIsLoading(true);
    getWebsiteConfig(groupId).then((data) => {
      const stadium = data.stadium;

      if (stadium && typeof stadium === "object") {
        if ("publishChants" in stadium)
          setPublishChants(!!stadium.publishChants);
        if ("isChantsPublished" in stadium)
          setIsChantsPublished(!!stadium.isChantsPublished);
        if ("publishPOM" in stadium) setPublishPOM(!!stadium.publishPOM);
        if ("publishPredictions" in stadium)
          setPublishPredictions(!!stadium.publishPredictions);
        if ("publishDonations" in stadium)
          setPublishDonations(!!stadium.publishDonations);
        if ("joinUrl" in stadium) setJoinUrl(stadium.joinUrl ?? "");
        if ("joinBenefit" in stadium) setJoinBenefit(stadium.joinBenefit ?? "");
        if ("donationPartner" in stadium) {
          const dp = stadium.donationPartner;
          // Normalize: old records stored the full partner object; new format is just the id string
          setDonationPartner(
            dp == null
              ? null
              : typeof dp === "object"
                ? String(dp.id)
                : String(dp),
          );
        }
        if ("donationBenefit" in stadium)
          setDonationBenefit(stadium.donationBenefit ?? "");

        setAcceptContentResponsibility(!!stadium.acceptContentResponsibility);
        setAcceptRightReservations(!!stadium.acceptRightReservations);
        setIsPublished(!!stadium.isPublished);
      }
      if (data?.isPublished) {
        useAdminDataStore.getState().setGettingStartedItemStatus(
          groupId,
          GUIDE_GROW,
          TOPIC_SETUP_CHANT_STADIUM,
          true,
        );
      }
      setIsLoading(false);
    });

    fbGetPartners(groupId).then((partners) => {
      setAllPartners(partners);
    });
  };

  useEffect(() => {
    fetchData(groupData?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "joinUrl") setJoinUrl(value);
    else if (name === "joinBenefit") setJoinBenefit(value);
    else if (name === "donationBenefit") setDonationBenefit(value);
  };

  const saveToFirebase = () => {
    if (!acceptContentResponsibility || !acceptRightReservations) {
      toast.error("Please accept the terms and conditions");
      return;
    }
    setIsSaving(true);
    const docRef = doc(db, "website", groupData?.id);
    const gd = groupData;
    const websiteUpdate = {
      clubName: gd.clubName || "",
      leagueName: gd.leagueName || "",
      groupName: gd.groupName || "",
      country: gd.country || "",
    };
    if (gd.clubId) websiteUpdate.clubId = gd.clubId;
    if (gd.leagueId) websiteUpdate.leagueId = gd.leagueId;
    const stadium = {
      publishChants,
      isChantsPublished,
      publishPOM,
      publishPredictions,
      publishDonations,
      joinUrl,
      joinBenefit,
      donationPartner,
      donationPartnerId: donationPartner ?? null,
      donationBenefit,
      isPublished,
      acceptContentResponsibility,
      acceptRightReservations,
    };
    setDoc(
      docRef,
      { ...websiteUpdate, configuration: gd.configuration || {}, stadium },
      { merge: true },
    )
      .then(() => {
        postUpdateCDNUtil(groupData?.id);
        invalidateStadiumCache(groupData?.groupKey);
        fetchData(groupData?.id);
        toast.success("Settings saved");
        setIsSaving(false);
      })
      .catch((err) => {
        console.error("Failed to save stadium settings:", err);
        toast.error("Failed to save. Please try again.");
        setIsSaving(false);
      });
  };

  const onDonationPartnerChange = (value) => {
    setDonationPartner(value == null || value === "" ? null : String(value));
  };

  if (isLoading) {
    return (
      <div className="stadium-setup-container">
        <ScreenHeader title="Setup" />
        <div
          style={{ marginTop: "var(--screen-general-margin)" }}
          className="stadium-setup bg-white px-3 px-md-4 py-4"
        >
          <SkeletonForm fields={8} showButtons={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="stadium-setup-container">
      <ScreenHeader title="Setup" />
      <div style={{ marginTop: "var(--screen-general-margin)" }}>
        <DescriptionText showBackground>
          Share your passion across the stadium. Publish your Stadium and Chants
          pages with real-time chants, predictions, player of match voting, a
          link to join your group and donation collection for your group or a
          partner. Post a QR code below around the stadium and ask your club to
          share on the scoreboard. Your capo can press/hold any chant from the
          app to dynamically flip the chant card for fans on the web. By
          publishing the Chants page you confirm all chants comply with your
          club and league code of conduct and are free from racist, homophobic,
          misogynistic, bigoted or otherwise aggressive content.
        </DescriptionText>
      </div>
      <div
        style={{ marginTop: "var(--screen-general-margin)" }}
        className="stadium-setup bg-white  px-3 px-md-4 py-4"
      >
        <StadiumSection id="pom" title="Player of Match">
          <p>
            Publish player of the match voting (via web) for those in the
            stadium who are not on the Chant app.
          </p>
          <p>
            If in a league where player of the match is not offered by default
            (leagues where the starting xi and subs are not available during the
            match) a leader will select eligible players via the app before
            voting opens in the 85th min.
          </p>
          <p>
            When voting is not active the page displays current and past player
            of match winners.
          </p>
          <div className="stadium-setup-checkbox-row">
            <ChantCheckbox
              checked={publishPOM}
              onChange={(e) => setPublishPOM(e.target.checked)}
              aria-label="Publish POM checkbox"
              tabIndex={2}
            />
            <div>
              <p className="stadium-setup-checkbox-row__label">
                Publish Player of Match Voting
              </p>
            </div>
          </div>
        </StadiumSection>

        <StadiumSection id="predictions" title="Match Predictions">
          <p>
            Show a match predictions widget on your Stadium page, allowing fans
            in the stadium to submit their score predictions for the current
            match.
          </p>
          <p>
            When no match is active the widget is hidden automatically. Results
            are visible to fans after the match.
          </p>
          <div className="stadium-setup-checkbox-row">
            <ChantCheckbox
              checked={publishPredictions}
              onChange={(e) => setPublishPredictions(e.target.checked)}
              aria-label="Publish predictions checkbox"
              tabIndex={2}
            />
            <div>
              <p className="stadium-setup-checkbox-row__label">
                Show Match Predictions
              </p>
            </div>
          </div>
        </StadiumSection>

        <StadiumSection id="join" title="Join">
          <p>
            When the page is accessed via mobile device we show a Join card you
            can use to direct fans to join your group. Consider sharing the
            mobile fan registration link allowing chant access as a non-member
            (with restricted app permissions) as an initial way to drive
            engagement. Fans can then be encouraged to upgrade to full
            membership.
          </p>
          <div className="stadium-setup-field-grid">
            <p className="stadium-setup-field-label">
              Add URL for &apos;Join&apos; button
            </p>
            <ChantInput
              value={joinUrl}
              onChange={handleInputChange}
              name="joinUrl"
              tabIndex={2}
              placeholder="https://…"
            />
            <p className="stadium-setup-field-label">
              Benefit of joining (brief)
            </p>
            <ChantInput
              value={joinBenefit}
              onChange={handleInputChange}
              name="joinBenefit"
              maxLength={150}
              placeholder="Max 150 characters"
              tabIndex={2}
              showCount
            />
          </div>
        </StadiumSection>

        <StadiumSection id="donations" title="Donations">
          <p>
            To collect donations, create a Partner in the Content / Partners
            section of the dashboard. Add special text below targeted to fans in
            the stadium to describe the value of the donation.
          </p>
          <p>
            Accepting donations requires a Stripe account set up through Chant.
            Request from support@chant.fan.
          </p>
          <div className="stadium-setup-checkbox-row">
            <ChantCheckbox
              checked={publishDonations}
              onChange={(e) => {
                setPublishDonations(e.target.checked);
                if (!e.target.checked) {
                  setDonationPartner(null);
                }
              }}
              aria-label="Publish donations checkbox"
              tabIndex={2}
            />
            <div>
              <p className="stadium-setup-checkbox-row__label">
                Publish Donations
              </p>
            </div>
          </div>
          {publishDonations && (
            <div className="stadium-setup-field-grid">
              <p className="stadium-setup-field-label">Select Partner</p>
              <ChantSelect
                placeholder="Select Partner"
                allowClear
                showSearch
                optionFilterProp="label"
                options={partnerSelectOptions}
                value={donationPartner ?? undefined}
                onChange={onDonationPartnerChange}
                tabIndex={2}
              />
              <p className="stadium-setup-field-label">
                Benefit of donating (brief)
              </p>
              <ChantInput
                value={donationBenefit}
                onChange={handleInputChange}
                name="donationBenefit"
                maxLength={150}
                placeholder="Max 150 characters"
                tabIndex={2}
                showCount
              />
            </div>
          )}
        </StadiumSection>

        <StadiumSection id="chants" title="Chants">
          <p>
            Publish your Chants page to display real-time chants during the
            match. Your capo can press and hold any chant from the app to
            dynamically flip the chant card for fans on the web.
          </p>
          <p>
            By publishing the Chants page you confirm all chants comply with
            your club and league code of conduct and are free from racist,
            homophobic, misogynistic, bigoted or otherwise aggressive content.
          </p>
          <div className="stadium-setup-checkbox-row">
            <ChantCheckbox
              checked={publishChants}
              onChange={(e) => setPublishChants(e.target.checked)}
              aria-label="Publish chants checkbox"
              tabIndex={2}
            />
            <div>
              <p className="stadium-setup-checkbox-row__label">
                Publish Chants
              </p>
            </div>
          </div>
        </StadiumSection>

        <StadiumSection id="access" title="Access (when published)">
          <div className="access-qr-cards">
            <div className="access-qr-card">
              <p className="access-qr-card__label">Stadium</p>
              <GroupQR
                ref={stadiumQrRef}
                url={stadiumPublicUrl}
                size={240}
                downloadName="stadium-qr"
              />
              <p className="access-qr-card__url">{stadiumPublicUrl}</p>
              <ChantButton
                type="default"
                icon={<DownloadOutlined />}
                size="small"
                onClick={() => stadiumQrRef.current?.download()}
                aria-label="Download stadium QR code"
              >
                Download
              </ChantButton>
            </div>
            <div className="access-qr-card">
              <p className="access-qr-card__label">Chants</p>
              <GroupQR
                ref={chantsQrRef}
                url={chantsPublicUrl}
                size={240}
                downloadName="chants-qr"
              />
              <p className="access-qr-card__url">{chantsPublicUrl}</p>
              <ChantButton
                type="default"
                icon={<DownloadOutlined />}
                size="small"
                onClick={() => chantsQrRef.current?.download()}
                aria-label="Download chants QR code"
              >
                Download
              </ChantButton>
            </div>
          </div>
        </StadiumSection>

        <StadiumSection id="publish" title="Publish">
          <div
            className="stadium-setup-checkbox-row"
            style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}
          >
            <ChantCheckbox
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
              aria-label="Publish stadium checkbox"
              tabIndex={2}
            />
            <div>
              <p
                className="stadium-setup-checkbox-row__label"
                style={{ margin: 0 }}
              >
                Stadium{" "}
                <span style={{ fontWeight: 400, opacity: 0.6, fontSize: 12 }}>
                  (/)
                </span>
              </p>
              <p style={{ margin: 0, fontSize: 12, opacity: 0.6 }}>
                {stadiumPublicUrl}
              </p>
            </div>
          </div>
          <div className="stadium-setup-checkbox-row">
            <ChantCheckbox
              checked={isChantsPublished}
              onChange={(e) => setIsChantsPublished(e.target.checked)}
              aria-label="Publish chants page checkbox"
              tabIndex={2}
            />
            <div>
              <p
                className="stadium-setup-checkbox-row__label"
                style={{ margin: 0 }}
              >
                Chants{" "}
                <span style={{ fontWeight: 400, opacity: 0.6, fontSize: 12 }}>
                  (/chants)
                </span>
              </p>
              <p style={{ margin: 0, fontSize: 12, opacity: 0.6 }}>
                {chantsPublicUrl}
              </p>
            </div>
          </div>
        </StadiumSection>

        <StadiumSection id="terms" title="Terms">
          <div
            className="stadium-setup-checkbox-row"
            style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}
          >
            <ChantCheckbox
              checked={acceptContentResponsibility}
              onChange={(e) => setAcceptContentResponsibility(e.target.checked)}
              aria-label="Accept content responsibility checkbox"
              tabIndex={2}
            />
            <p style={{ margin: 0 }}>
              {groupName} and our leaders are responsible for all content.
            </p>
          </div>
          <div className="stadium-setup-checkbox-row">
            <ChantCheckbox
              checked={acceptRightReservations}
              onChange={(e) => setAcceptRightReservations(e.target.checked)}
              aria-label="Accept right reservations checkbox"
              tabIndex={2}
            />
            <p style={{ margin: 0 }}>
              Chant reserves the right to add the Chant brand and Sponsors to
              these pages.
            </p>
          </div>
        </StadiumSection>

        <div className="stadium-setup-footer-buttons">
          <ChantButton
            type="primary"
            disabled={isSaving}
            loading={isSaving}
            onClick={saveToFirebase}
            style={{ width: "100px" }}
          >
            Save
          </ChantButton>
        </div>
      </div>
    </div>
  );
};

export default StadiumSetup;
